import { useQuery, useMutation } from "@apollo/client/react";
import SelectTeam from "@components/account/Teams/SelectTeam";
import { DSCheckbox } from "@components/design-system/molecules";
import { DSModal } from "@components/design-system/organisms/DSModal";
import { faArrowCircleRight } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TRANSFER_NOTES } from "@graphql/mutations/TransferNotes";
import getPatientsQuery from "@graphql/queries/GetPatients";
import { useTeam, useAuth, useNoteMultiselectContext } from "@hooks";
import Twemoji from "@src/Twemoji";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useState } from "react";

import { evictNoteCache, truncate } from "../../../utils";
import NoteCreatedAt from "../../notebook/note/NoteCreatedAt";
import { alert } from "../Alert";
import TeamUserSelect from "../dropdown/TeamUserSelect";

export function TransferNotesModal({
  notes,
  shouldShowTransferModal,
  setShouldShowTransferModal,
  wasAutoPrompted = false,
  refetch,
  bypassContext = false,
}) {
  // filter null notes passed in
  notes = notes.filter(Boolean);
  // Get multiselect context
  const {
    selectedNoteUuids: selectedNoteUuidsFromContext,
    resetSelectedNotesList,
    numPatientNotes,
  } = useNoteMultiselectContext();

  const selectedNoteUuids = bypassContext
    ? notes.map((note) => note.uuid)
    : Object.keys(selectedNoteUuidsFromContext);

  const { currentTeam, userTeams } = useTeam();
  const { userUuid } = useAuth();
  const { transferNewNotes } = useFlags();

  const [newUser, setNewUser] = useState(userUuid);
  const [newTeam, setNewTeam] = useState(currentTeam);
  const [applyCustomDefaults, setApplyCustomDefaults] =
    useState(transferNewNotes);

  // Query patients to possibly disable transfer if patients are still loading
  // As you cannot transfer patient notes
  const { loading: patientsLoading } = useQuery(getPatientsQuery, {
    fetchPolicy: "network-only",
  });
  const [transferNotes, { loading }] = useMutation(TRANSFER_NOTES);

  const numSelectedNotes =
    (selectedNoteUuids && selectedNoteUuids.length) || 0;

  const handleUserChange = (_, value) => {
    setNewUser(value);
  };
  const showApplyCustomDefaults =
    notes.filter(
      (note) =>
        selectedNoteUuids?.includes(note?.uuid) &&
        ["medical_record", "multipet", "packmode"].includes(
          note.jobType,
        ),
    ).length > 0;

  const confirmAction = async () => {
    if (numPatientNotes > 0) {
      alert(
        "error",
        "Unsuccessful Note Transfer: You can't transfer notes associated with one of your patients.",
      );
      return;
    }
    try {
      const { data } = await transferNotes({
        variables: {
          noteUuids: selectedNoteUuids,
          newTeamUuid: newTeam && newTeam.uuid,
          newOwnerUuid: newUser,
          applyCustomDefaults,
        },
        update: evictNoteCache,
      });

      if (
        data.transferNoteOwnership.failedNotes &&
        data.transferNoteOwnership.failedNotes.length
      ) {
        alert(
          "error",
          "Some notes failed to transfer. Are you sure you are the owner of all the notes you are transferring?",
        );
        mixpanel.track(
          `Failed to transfer note${
            selectedNoteUuids.length > 1 ? "s" : ""
          }`,
          {
            transferredNoteUuids: selectedNoteUuids,
            failedNotes: data.transferNoteOwnership.failedNotes,
            newNoteOwner: newUser,
            newNoteTeam: newTeam && newTeam.uuid,
            previousNoteOwner: localStorage.getItem("user_uuid"),
          },
        );
      } else {
        alert(
          "success",
          `Note${
            selectedNoteUuids.length > 1 ? "s" : ""
          } transferred successfully!`,
        );
        mixpanel.track("Transferred Note", {
          transferredNoteUuids: selectedNoteUuids,
          newNoteOwner: newUser,
          newNoteTeam: newTeam && newTeam.uuid,
          previousNoteOwner: localStorage.getItem("user_uuid"),
        });
      }
      if (refetch) refetch();
      resetSelectedNotesList();
      setShouldShowTransferModal(false);
    } catch (error) {
      alert(
        "error",
        "Unsuccessful Note Transfer. Please try again later. If the issue persists, please contact support.",
      );
      mixpanel.track("Transfer Note Error", {
        transferredNoteUuids: selectedNoteUuids,
        newNoteOwner: newUser,
        previousNoteOwner: localStorage.getItem("user_uuid"),
        transferNoteError: error,
      });
    }
  };

  return (
    <DSModal
      isOpen={shouldShowTransferModal}
      onClose={() => setShouldShowTransferModal(false)}
      title={
        numSelectedNotes > 1
          ? "Transfer these notes?"
          : "Transfer this note?"
      }
      actions={[
        {
          label: "Cancel",
          variant: "cancel",
          onClick: () => setShouldShowTransferModal(false),
        },
        {
          label: "Transfer",
          variant: "primary",
          onClick: confirmAction,
          disabled: loading || patientsLoading,
          dataTestId: "confirm-button",
        },
      ]}
      width="w-[50vw] max-w-[800px]"
      dataTestId="transfer-notes-modal"
    >
      <h5>
        Which team member would you like to{" "}
        <strong>
          transfer{" "}
          {numSelectedNotes > 1
            ? `these ${numSelectedNotes} notes`
            : "this note"}
        </strong>{" "}
        to?
      </h5>
      <div className="flex flex-row items-center space-x-4 my-10">
        <div className="flex-1">
          {notes &&
            notes.map((note) => {
              if (selectedNoteUuids?.includes(note?.uuid)) {
                return (
                  <h5
                    key={note.uuid}
                    className="text-sm py-1 border p-2 rounded-standard border-secondary bg-secondary my-1"
                  >
                    <Twemoji emoji={"📝"} />{" "}
                    <span className="font-medium">
                      {note.patient ? note.patient.name : ""}
                    </span>{" "}
                    <span>
                      <span className="font-medium">
                        {truncate(note.title, 40) || "Untitled Note"}
                      </span>{" "}
                      <NoteCreatedAt
                        createdAt={note.createdAt}
                        size="sm"
                      />
                    </span>
                  </h5>
                );
              }
              return null;
            })}
        </div>
        <FontAwesomeIcon
          className="text-secondary"
          icon={faArrowCircleRight}
          size="2x"
        />
        <div className="flex-1 flex flex-col gap-sm">
          {userTeams?.length > 1 && (
            <div className="flex-1 min-w-0">
              <SelectTeam
                currentTeam={newTeam}
                setCurrentTeam={setNewTeam}
                dropdownWidth="w-full"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <TeamUserSelect
              team={newTeam}
              value={newUser}
              onChange={handleUserChange}
              showLabel
              label="Select Member"
              placeholder="Select member"
              searchPlaceholder="Search team"
              width="w-full"
              dataTestId="transfer-notes-user-select"
            />
          </div>
        </div>
      </div>
      {showApplyCustomDefaults && (
        <div>
          <label className="flex items-center mt-4 gap-sm">
            <DSCheckbox
              checked={applyCustomDefaults}
              onChange={(e) =>
                setApplyCustomDefaults(e.target.checked)
              }
              dataTestId="apply-custom-defaults-checkbox"
            />
            <div className="text-xs leading-none">
              Apply their custom defaults to the note*
              <br />
              <small>
                *This will overwrite any edits made to the note
              </small>
            </div>
          </label>
        </div>
      )}

      {wasAutoPrompted && (
        <div className="text-xs mt-4 text-secondary">
          If you don't want to be prompted to transfer newly created
          notes you can disable this feature in your Account Settings
          under Teams.
        </div>
      )}
    </DSModal>
  );
}
